import React, { useState } from "react";
import styled from "@emotion/styled";
import {
  fontSize,
  fontWeight,
  spacing,
  borderRadius,
  border as borderToken,
  interactive,
  surface,
  text,
} from "@atlas-int/tokens";
import PreviewSection from "../components/PreviewSection";
import CodeBlock from "../components/CodeBlock";

import {
  GraphLoading,
  GraphNotice,
  GraphNoticeType,
  VisualizationContent,
  CategoryLabels,
} from "@atlas-int/viz/shared";
import type { CategoryDatum } from "@atlas-int/viz/shared";

// ─── Styled helpers ──────────────────────────────────────────────────────────

const DemoRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[8]};
  align-items: flex-start;
`;

const DemoBox = styled.div<{ $width?: string; $height?: string }>`
  position: relative;
  width: ${({ $width }) => $width ?? "260px"};
  height: ${({ $height }) => $height ?? "180px"};
  border: 1px solid ${borderToken.default};
  border-radius: ${borderRadius.base};
  overflow: hidden;
  background-color: ${surface.raised};
`;

const DemoLabel = styled.div`
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.semibold};
  color: ${text.secondary};
  margin-bottom: ${spacing[2]};
`;

const DemoGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const SubSection = styled.div`
  margin-bottom: ${spacing[12]};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SubTitle = styled.h3`
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.semibold};
  color: ${text.primary};
  margin: 0 0 ${spacing[4]} 0;
`;

const Description = styled.p`
  font-size: ${fontSize.base};
  color: ${text.secondary};
  margin: 0 0 ${spacing[6]} 0;
`;

const Note = styled.div`
  font-size: ${fontSize.sm};
  color: ${text.muted};
  padding: ${spacing[4]} ${spacing[6]};
  background-color: ${surface.panel};
  border-radius: ${borderRadius.base};
  margin-top: ${spacing[6]};
`;

// ─── Mock data for CategoryLabels ───────────────────────────────────────────

const mockCategories: CategoryDatum[] = [
  { id: "animal", color: "#E4BA29", name: "Animal Products" },
  { id: "vegetable", color: "#6CB13F", name: "Vegetable Products" },
  { id: "minerals", color: "#994D14", name: "Minerals" },
  { id: "chemicals", color: "#D94D4D", name: "Chemicals" },
  { id: "textiles", color: "#B561D9", name: "Textiles" },
  { id: "metals", color: "#5882BD", name: "Metals" },
  { id: "machinery", color: "#2D97AD", name: "Machinery" },
  { id: "electronics", color: "#45C2EF", name: "Electronics" },
];

// Override grid positioning for standalone rendering
const CategoryLabelsWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: ${spacing[4]};
  border: 1px solid ${borderToken.default};
  border-radius: ${borderRadius.base};
  background-color: ${surface.raised};

  /* Override CategoryLabels grid-row/column positioning for standalone demo */
  & > div {
    grid-row: unset;
    grid-column: unset;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }
`;

// ─── GraphLoading Section ────────────────────────────────────────────────────

const GraphLoadingDemo: React.FC = () => (
  <SubSection>
    <SubTitle>GraphLoading</SubTitle>
    <Description>
      Spinner overlay backed by LoadingOverlay. Positioned absolutely within a relative container.
    </Description>
    <DemoRow>
      <DemoGroup>
        <DemoLabel>Default</DemoLabel>
        <DemoBox>
          <GraphLoading />
        </DemoBox>
      </DemoGroup>
      <DemoGroup>
        <DemoLabel>Custom z-index</DemoLabel>
        <DemoBox>
          <GraphLoading zIndex={100} />
        </DemoBox>
      </DemoGroup>
    </DemoRow>
    <CodeBlock
      code={`import { GraphLoading } from "@atlas-int/viz/shared";

<GraphLoading />
<GraphLoading zIndex={100} />`}
    />
  </SubSection>
);

// ─── GraphNotice Section ─────────────────────────────────────────────────────

const GraphNoticeDemo: React.FC = () => {
  const noticeTypes: { type: GraphNoticeType; label: string }[] = [
    { type: GraphNoticeType.Error, label: "Error" },
    { type: GraphNoticeType.NoData, label: "No Data" },
    { type: GraphNoticeType.CountrySelectionRequired, label: "Country Required" },
    { type: GraphNoticeType.ComplexityNotAvailable, label: "Complexity N/A" },
  ];

  return (
    <SubSection>
      <SubTitle>GraphNotice</SubTitle>
      <Description>
        Static notice messages for error and empty states. Positioned absolutely.
      </Description>
      <DemoRow>
        {noticeTypes.map(({ type, label }) => (
          <DemoGroup key={type}>
            <DemoLabel>{label}</DemoLabel>
            <DemoBox $width="280px">
              <GraphNotice graphNoticeType={type} />
            </DemoBox>
          </DemoGroup>
        ))}
      </DemoRow>
      <CodeBlock
        code={`import { GraphNotice, GraphNoticeType } from "@atlas-int/viz/shared";

<GraphNotice graphNoticeType={GraphNoticeType.Error} />
<GraphNotice graphNoticeType={GraphNoticeType.NoData} />`}
      />
    </SubSection>
  );
};

// ─── VisualizationContent Section ────────────────────────────────────────────

const VisualizationContentDemo: React.FC = () => {
  const [state, setState] = useState<"data" | "loading" | "error" | "nodata">(
    "data",
  );

  const SampleChart = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    font-size: ${fontSize.base};
    color: ${text.secondary};
  `;

  const ToggleRow = styled.div`
    display: flex;
    gap: ${spacing[4]};
    margin-bottom: ${spacing[6]};
  `;

  const ToggleButton = styled.button<{ $active?: boolean }>`
    padding: ${spacing[2]} ${spacing[6]};
    font-size: ${fontSize.sm};
    font-weight: ${fontWeight.semibold};
    border: 1px solid ${borderToken.default};
    border-radius: ${borderRadius.base};
    cursor: pointer;
    background-color: ${({ $active }) =>
      $active ? interactive.default : surface.raised};
    color: ${({ $active }) =>
      $active ? text.onDark : text.primary};

    &:hover {
      background-color: ${({ $active }) =>
        $active ? interactive.hover : surface.panel};
    }
  `;

  return (
    <SubSection>
      <SubTitle>VisualizationContent</SubTitle>
      <Description>
        Standardized content area that handles loading, error, and no-data states.
        Replaces ad-hoc conditional rendering in each chart.
      </Description>
      <ToggleRow>
        {(["data", "loading", "error", "nodata"] as const).map((s) => (
          <ToggleButton key={s} $active={state === s} onClick={() => setState(s)}>
            {s === "nodata" ? "No Data" : s.charAt(0).toUpperCase() + s.slice(1)}
          </ToggleButton>
        ))}
      </ToggleRow>
      <DemoBox $width="100%" $height="200px">
        <VisualizationContent
          hasError={state === "error"}
          hasNoData={state === "nodata"}
          loading={state === "loading"}
        >
          <SampleChart>[Chart content renders here]</SampleChart>
        </VisualizationContent>
      </DemoBox>
      <CodeBlock
        code={`import { VisualizationContent, useVisualizationState } from "@atlas-int/viz/shared";

const vizState = useVisualizationState({ loading, error, data, previousData });

<VisualizationContent
  hasError={vizState.hasError}
  hasNoData={vizState.hasNoData}
  loading={loading}
>
  <Chart data={vizState.displayData} />
</VisualizationContent>`}
      />
    </SubSection>
  );
};

// ─── CategoryLabels Section ──────────────────────────────────────────────────

const CategoryLabelsDemo: React.FC = () => {
  const [hiddenCategories, setHiddenCategories] = useState<string[]>([]);

  return (
    <SubSection>
      <SubTitle>CategoryLabels</SubTitle>
      <Description>
        Toggleable legend labels for chart categories (sectors, regions, clusters).
        Supports toggle, isolate, and reset interactions. Click a label to hide it,
        right-click for "Keep Only".
      </Description>
      <DemoRow>
        <DemoGroup>
          <DemoLabel>With toggle (click labels to hide/show)</DemoLabel>
          <CategoryLabelsWrapper>
            <CategoryLabels
              categories={mockCategories}
              allowToggle={true}
              hiddenCategories={hiddenCategories}
              setHiddenCategories={setHiddenCategories}
              resetText="Show All Sectors"
              fullWidth={true}
            />
          </CategoryLabelsWrapper>
        </DemoGroup>
      </DemoRow>
      <DemoRow style={{ marginTop: spacing[6] }}>
        <DemoGroup>
          <DemoLabel>Static (no toggle)</DemoLabel>
          <CategoryLabelsWrapper>
            <CategoryLabels
              categories={mockCategories.slice(0, 4)}
              allowToggle={false}
              fullWidth={true}
            />
          </CategoryLabelsWrapper>
        </DemoGroup>
      </DemoRow>
      <CodeBlock
        code={`import { CategoryLabels } from "@atlas-int/viz/shared";
import { getCategoriesForChartLegend } from "@atlas-int/viz";
import { LegendLabels } from "@atlas-int/data";

const categories = getCategoriesForChartLegend({ view, productClass });

<CategoryLabels
  legendLabel={LegendLabels.Sector}
  categories={categories}
  allowToggle={true}
  hiddenCategories={hiddenCategories}
  setHiddenCategories={setHiddenCategories}
  resetText="Show All Sectors"
  fullWidth={true}
/>`}
      />
    </SubSection>
  );
};

// ─── LegendRow Section ───────────────────────────────────────────────────────

const LegendRowDemo: React.FC = () => (
  <SubSection>
    <SubTitle>LegendRow</SubTitle>
    <Description>
      Wraps legend content (CategoryLabels, color scales, etc.) in the standard
      bottom-row grid container. Must be used inside a VizGrid layout.
    </Description>
    <Note>
      LegendRow depends on VizGrid's CSS grid areas and cannot be rendered standalone.
      In explore pages, it is always a sibling of VisualizationContentContainer.
    </Note>
    <CodeBlock
      code={`import { LegendRow } from "visualization/components";

<LegendRow>
  <CategoryLabels
    legendLabel={LegendLabels.Sector}
    categories={categoriesForLegend}
    allowToggle={true}
    hiddenCategories={hiddenCategories}
    setHiddenCategories={setHiddenCategories}
    resetText="Show All Sectors"
    fullWidth={true}
  />
</LegendRow>`}
    />
  </SubSection>
);

// ─── Main Section ────────────────────────────────────────────────────────────

const VizComponentsSection: React.FC = () => (
  <>
    <PreviewSection
      id="viz-content"
      title="VisualizationContent"
      description="Chart area with loading/error handling"
    >
      <VisualizationContentDemo />
    </PreviewSection>

    <PreviewSection
      id="graph-loading"
      title="GraphLoading"
      description="Loading spinner overlay"
    >
      <GraphLoadingDemo />
    </PreviewSection>

    <PreviewSection
      id="graph-notice"
      title="GraphNotice"
      description="Error/no-data notice"
    >
      <GraphNoticeDemo />
    </PreviewSection>

    <PreviewSection
      id="category-labels"
      title="CategoryLabels"
      description="Toggleable legend labels"
    >
      <CategoryLabelsDemo />
    </PreviewSection>

    <PreviewSection
      id="legend-row"
      title="LegendRow"
      description="Standard legend container"
    >
      <LegendRowDemo />
    </PreviewSection>
  </>
);

export default VizComponentsSection;
